/**
 * Morse-Ton: die Messzeile ins Server-Journal. (08.10.2026)
 *
 * Martin (Morse-Trainer, Lautsprecher): (a) "manchmal kommt der erste Buchstabe langsam", (b) das Tempo wird "mal
 * schneller, mal langsamer", auch nach Verlangsamen. Der Zeitplan ist exakt und konstant (shared/morse.ts, geprüft).
 * Zwei Verdächtige, die die Zeile GETRENNT ausweist:
 *
 *   ANLAUF (kalter `resume()`):   kalt, vor, resume=<ms>, anlauf=<ms>/<ok|frist>, vorlauf, anfang
 *     - `resume` = wie lange das Wecken brauchte; `anlauf` = wie lange danach die Uhr des Kontexts stand
 *       (morse-anlauf.ts); `anfang` = Hinken der Kontext-Uhr in den ersten ~250 ms des Plans (auch warm sichtbar).
 *   TAKT (Uhr des Kontexts gegen die der Seite, während der Plan läuft):   takt, wackel, proben, ctx, seite
 *     - `takt=+6ms/+0.24%` = Kontext-Uhr hinkt am Ende des Plans so viel hinter der Seiten-Uhr her (+ = die Töne
 *       klingen langsamer, − = schneller); `wackel` = Abstand dieses Hinkens von der Geraden erste Probe-Ende
 *       (alle ~250 ms gemessen; der Anlauf der ersten 250 ms steht in `anfang`): gleichmäßiges Hinken = hohes `takt`, `wackel` ~0; Ruckeln/Stocken mittendrin = hohes `wackel`.
 *
 * Nur Zahlen und Stichwörter, nie Inhalt (kein Text, keine Zeichen). Weg: meldeSpur → /api/debug/ptt-timing, wie
 * alle Client-Diagnosen (`[morse-ton] ...` im Journal). Nur Konto 1 (shared/morse-konto.ts). Rate-Limit: höchstens
 * 12 Zeilen je Minute für kalte Starts, 6 für warme — was darüber liegt, wird gezählt (`ausgelassen=N`) und der
 * nächsten Zeile beigelegt. Die Hörspur spielt viele kurze Pläne hintereinander; das Journal soll nicht überlaufen.
 *
 * Grenzen: die Proben nehmen beide Uhren im selben Atemzug (Hauptfaden). Bei Plänen unter 200 ms steht `takt=-`;
 * unter ~1 s sind ±3 ms Messrauschen (Takt der Kontext-Uhr) normal.
 */
import { morseFuerKonto } from "../../shared/morse-konto.js";
import type { AnlaufBefund } from "./morse-anlauf.js";
import { meldeSpur } from "./mikro-spur.js";

/** Der Bereich in der Journalzeile. */
const MORSE_MESS_BEREICH = "morse-ton";
/** Abstand der Proben während des Plans (ms). */
const MORSE_PROBE_ALLE_MS = 250;
/** Kürzer als das ist ein Plan zu kurz für ein Takt-Urteil. */
const MORSE_TAKT_MIN_MS = 200;
/** Rate-Limit: Fenster und Höchstzahl je Art. */
export const MORSE_MESS_FENSTER_MS = 60_000;
export const MORSE_MESS_MAX_KALT = 12;
export const MORSE_MESS_MAX_WARM = 6;

/** Beide Uhren im selben Moment: die des Kontexts (ms) und die der Seite (ms). */
export interface TaktProbe {
  ctxMs: number;
  seiteMs: number;
}

interface TaktBefund {
  /** Dauer zwischen erster und letzter Probe, auf der Uhr der Seite und der des Kontexts (ms). */
  seiteMs: number;
  ctxMs: number;
  /** Hinken der Kontext-Uhr am Ende (ms, + = Kontext-Uhr geht nach) und in Prozent der Seiten-Dauer. */
  hinktMs: number;
  hinktProzent: number;
  /** Hinken bei der ersten Probe NACH dem Beginn (ms) — der Anlauf-Anteil. `null`, wenn es nur Anfang und Ende gab. */
  anfangMs: number | null;
  /** Abstand des Hinkens von der Geraden erste Probe-Ende (ms) — Ruckeln statt gleichmäßigem Hinken. */
  wackelMs: number;
  proben: number;
}

/** Aus den Proben (erste = Beginn des Plans) die Takt-Abweichung rechnen. Rein. `null`: zu kurz oder zu wenig. */
export function taktBefund(proben: readonly TaktProbe[]): TaktBefund | null {
  if (proben.length < 2) return null;
  const a = proben[0];
  const z = proben[proben.length - 1];
  const seiteMs = z.seiteMs - a.seiteMs;
  if (!(seiteMs >= MORSE_TAKT_MIN_MS)) return null;
  const hinkt = proben.map((p) => p.seiteMs - a.seiteMs - (p.ctxMs - a.ctxMs));
  const ende = hinkt[hinkt.length - 1];
  // Ruckeln = Abstand von der Geraden "erste Probe nach dem Beginn -> Ende": gleichmäßiges Hinken (die Kontext-Uhr
  // geht um 1 % nach) ist eine Gerade, ein Stocken mittendrin nicht. Die ersten ~250 ms (Anlauf) zählen NICHT
  // dazu — sie stehen in `anfangMs`. Mit weniger als drei Proben lässt sich nichts über Ruckeln sagen: 0.
  let wackelMs = 0;
  if (proben.length >= 3) {
    const s1 = proben[1].seiteMs - a.seiteMs;
    const dt = seiteMs - s1;
    if (dt > 0) {
      const rest = proben.slice(1).map((p, i) => hinkt[i + 1] - (hinkt[1] + ((ende - hinkt[1]) * (p.seiteMs - a.seiteMs - s1)) / dt));
      wackelMs = Math.max(...rest) - Math.min(...rest);
    }
  }
  return {
    seiteMs,
    ctxMs: z.ctxMs - a.ctxMs,
    hinktMs: ende,
    hinktProzent: (ende / seiteMs) * 100,
    anfangMs: proben.length >= 3 ? hinkt[1] : null,
    wackelMs,
    proben: proben.length,
  };
}

/** Nimmt Proben, solange der Plan läuft: eine beim Beginn, dann alle `MORSE_PROBE_ALLE_MS`, eine am Ende. */
export class TaktMesser {
  private proben: TaktProbe[] = [];
  private uhr: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly nimm: () => TaktProbe) {}

  /** Beginn des Plans: erste Probe, dann im Takt weiter. */
  start(): void {
    this.proben = [this.nimm()];
    const weiter = (): void => {
      this.uhr = setTimeout(() => {
        this.proben.push(this.nimm());
        weiter();
      }, MORSE_PROBE_ALLE_MS);
    };
    weiter();
  }

  /** Ende: letzte Probe, Timer weg, Befund. Mehrfach rufbar (danach `null`). */
  stopp(): TaktBefund | null {
    if (this.uhr) clearTimeout(this.uhr);
    this.uhr = null;
    if (this.proben.length === 0) return null;
    this.proben.push(this.nimm());
    const befund = taktBefund(this.proben);
    this.proben = [];
    return befund;
  }
}

/** Alles, was eine Zeile trägt. */
export interface MessDaten {
  kalt: boolean;
  /** Zustand des Kontexts vor dem Wecken und am Ende. */
  vor: string;
  nach: string;
  /** Wie lange `resume()` brauchte (ms); `null`: der Kontext lief schon. */
  resumeMs: number | null;
  /** Der Anlauf (nur kalt). */
  anlauf: AnlaufBefund | null;
  vorlaufMs: number;
  takt: TaktBefund | null;
  /** Länge des Plans (ms) und Zahl der Töne. */
  planMs: number;
  toene: number;
  /** `baseLatency` / `outputLatency` des Kontexts (Sekunden), falls der Browser sie kennt. */
  base: number | null;
  out: number | null;
  /** Ergebnis von `spiele()`. */
  ende: string;
  /** Sekunden seit dem Ende des letzten Tons dieser Seite; `null`: noch keiner. */
  leerS: number | null;
  /** Weg der letzten Hi-Fi-Wiedergabe (playback-hifi.ts) — ein Etikett, nicht der Weg im Moment des Tons. */
  weg: string | null;
  /** Ausgabegerät laut Kontext (`sinkId`): std / gewaehlt / none … oder `-`. */
  sink: string;
  ort: "app" | "web";
  ausgelassen: number;
}

/** Ein Wort ohne Leerzeichen und Sonderzeichen für das Journal. */
function wort(v: unknown): string {
  const t = typeof v === "string" ? v.trim().replace(/[^A-Za-z0-9_-]/g, "") : "";
  return t.slice(0, 24) || "-";
}

const ms = (n: number | null): string => (n === null || !Number.isFinite(n) ? "-" : `${Math.round(n)}ms`);
const sekMs = (s: number | null | undefined): string => (typeof s === "number" && Number.isFinite(s) ? ms(s * 1000) : "-");
/** Mit Vorzeichen, auf `stellen` gerundet — nie "-0". */
const mitVorzeichen = (n: number, stellen = 0): string => {
  const r = Number(n.toFixed(stellen));
  return `${r < 0 ? "-" : "+"}${Math.abs(r).toFixed(stellen)}`;
};

/** Die Zeile (ohne `[morse-ton] `, das setzt `meldeSpur`). Rein. */
export function messZeile(d: MessDaten): string {
  const t = d.takt;
  const teile = [
    `kalt=${d.kalt ? "ja" : "nein"}`,
    `vor=${wort(d.vor)}`,
    `resume=${ms(d.resumeMs)}`,
    `anlauf=${d.anlauf ? `${ms(d.anlauf.ms)}/${d.anlauf.ergebnis}` : "-"}`,
    `vorlauf=${ms(d.vorlaufMs)}`,
    `anfang=${t && t.anfangMs !== null ? `${mitVorzeichen(t.anfangMs)}ms` : "-"}`,
    `takt=${t ? `${mitVorzeichen(t.hinktMs)}ms/${mitVorzeichen(t.hinktProzent, 2)}%` : "-"}`,
    `wackel=${t ? ms(t.wackelMs) : "-"}`,
    `proben=${t ? t.proben : "-"}`,
    `ctx=${t ? ms(t.ctxMs) : "-"}`,
    `seite=${t ? ms(t.seiteMs) : "-"}`,
    `plan=${ms(d.planMs)}`,
    `toene=${d.toene}`,
    `base=${sekMs(d.base)}`,
    `out=${sekMs(d.out)}`,
    `ende=${wort(d.ende)}/${wort(d.nach)}`,
    `leer=${d.leerS === null ? "-" : `${Math.round(d.leerS)}s`}`,
    `aus=${wort(d.weg)}`,
    `sink=${wort(d.sink)}`,
    `ort=${d.ort}`,
  ];
  if (d.ausgelassen > 0) teile.push(`ausgelassen=${d.ausgelassen}`);
  return teile.join(" ");
}

/** Das Ausgabegerät als Kategorie — nie eine Geräte-Kennung. Chromium kennt `sinkId`, Safari nicht (`-`). */
export function sinkArt(c: unknown): string {
  const s = (c as { sinkId?: unknown } | null)?.sinkId;
  if (typeof s === "string") return s === "" ? "std" : "gewaehlt";
  if (s && typeof s === "object") {
    const art = wort((s as { type?: unknown }).type);
    return art === "-" ? "obj" : art;
  }
  return "-";
}

/** Rate-Limit: kalte Starts sind die Fälle, um die es geht — sie bekommen mehr Platz als warme. */
export class MessBegrenzer {
  private zeiten: number[] = [];
  private aus = 0;

  /** Darf jetzt eine Zeile hinaus? Zählt, was nicht hinaus darf. */
  darf(kalt: boolean, jetzt: number): boolean {
    this.zeiten = this.zeiten.filter((t) => jetzt - t < MORSE_MESS_FENSTER_MS);
    const grenze = kalt ? MORSE_MESS_MAX_KALT : MORSE_MESS_MAX_WARM;
    if (this.zeiten.length >= grenze) {
      this.aus += 1;
      return false;
    }
    this.zeiten.push(jetzt);
    return true;
  }

  /** Wie viele seit der letzten Zeile ausgelassen wurden — und zurück auf 0. */
  nimmAusgelassen(): number {
    const n = this.aus;
    this.aus = 0;
    return n;
  }
}

/** Die Zeile ins Server-Journal — nur für Konto 1. Wirft nie. */
export function meldeMorseTon(zeile: string, uid: number | null | undefined = (globalThis as { __voiceV8Uid?: number }).__voiceV8Uid): void {
  if (!morseFuerKonto(uid)) return;
  meldeSpur(MORSE_MESS_BEREICH, zeile);
}
